import { scoreTone } from '../lib/format.js';

/**
 * The audit trail behind a Bull Score: one bar per weighted component.
 *
 * @param {{breakdown?: {components?: Record<string, {score: number, weight: number, detail?: string}>,
 *   rebalanced?: boolean}}} props
 */
export default function ScoreBreakdown({ breakdown }) {
  const components = Object.entries(breakdown?.components ?? {});

  if (components.length === 0) {
    return (
      <section className="panel">
        <header className="panel-heading">
          <h2 className="panel-title">Score composition</h2>
        </header>
        <p className="px-4 py-5 text-sm text-slate-500">No component breakdown published.</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <header className="panel-heading">
        <h2 className="panel-title">Score composition</h2>
        {breakdown?.rebalanced ? (
          <span
            className="pill border-caution/40 bg-caution/10 text-caution"
            title="Fundamental inputs were unavailable, so the remaining weights were renormalised."
          >
            weights rebalanced
          </span>
        ) : null}
      </header>

      <ul className="flex flex-col gap-3.5 px-4 py-4">
        {components.map(([name, entry]) => {
          const tone = scoreTone(entry.score);
          const width = Math.min(100, Math.max(0, Number(entry.score) || 0));
          return (
            <li key={name} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between text-xs">
                <span className="uppercase tracking-wider text-slate-400">
                  {name.replace(/_/g, ' ')}
                  <span className="ml-2 font-mono text-[10px] text-slate-600">
                    w{Math.round(entry.weight ?? 0)}
                  </span>
                </span>
                <span className={`font-mono ${tone.text}`}>
                  {Number.isFinite(entry.score) ? Math.round(entry.score) : '—'}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-terminal-800">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${width}%`, backgroundColor: tone.stroke }}
                />
              </div>
              {entry.detail ? (
                <span className="text-[11px] leading-snug text-slate-600">{entry.detail}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
